import React, { useState } from 'react';
import { Hammer, Activity, Play, AlertOctagon } from 'lucide-react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

type FatigueAnalysisType = 'miners_rule' | 'paris_law' | 'inverse_power_law' | 'steinberg_vibration';

const FatigueAnalysis: React.FC = () => {
    const [analysisType, setAnalysisType] = useState<FatigueAnalysisType>('miners_rule');
    const [params, setParams] = useState<{ [key: string]: any }>({});
    const [result, setResult] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    // Special state for Miner's Rule table
    const [loadEvents, setLoadEvents] = useState<{ cycles_actual: number, cycles_to_failure: number }[]>([{ cycles_actual: 0, cycles_to_failure: 0 }]);

    const analysisOptions: { value: FatigueAnalysisType, label: string }[] = [
        { value: 'miners_rule', label: 'Miner\'s Rule (Cumulative Damage)' },
        { value: 'paris_law', label: 'Paris Law (Crack Growth)' },
        { value: 'inverse_power_law', label: 'Inverse Power Law (Stress)' },
        { value: 'steinberg_vibration', label: 'Steinberg (PCB Vibration)' },
    ];

    const getFields = (type: FatigueAnalysisType) => {
        switch (type) {
            case 'paris_law': return [
                { key: 'initial_crack_m', label: 'Initial Crack (m)', type: 'number', placeholder: '0.001' },
                { key: 'critical_crack_m', label: 'Critical Crack (m)', type: 'number', placeholder: '0.01' },
                { key: 'cycles', label: 'Cycles (N)', type: 'number', placeholder: '100000' },
                { key: 'delta_k_mpa_sqrtm', label: 'Stress Intensity ΔK (MPa√m)', type: 'number', placeholder: '15.0' }
            ];
            case 'inverse_power_law': return [
                { key: 'stress_use', label: 'Use Stress (V/MPa)', type: 'number', placeholder: '5.0' },
                { key: 'stress_test', label: 'Test Stress (V/MPa)', type: 'number', placeholder: '10.0' },
                { key: 'n_exponent', label: 'Exponent (n)', type: 'number', placeholder: '3.0' }
            ];
            case 'steinberg_vibration': return [
                { key: 'max_displacement_in', label: 'Max Displacement (in)', type: 'number', placeholder: '0.002' },
                { key: 'component_length_in', label: 'Component Length (in)', type: 'number', placeholder: '0.5' },
                { key: 'pcb_thickness_in', label: 'PCB Thickness (in)', type: 'number', placeholder: '0.062' }
            ];
            default: return [];
        }
    };

    const handleCalculate = async () => {
        setLoading(true);
        setResult(null);
        try {
            let payloadParams = { ...params };

            // Handle special input for Miner's Rule
            if (analysisType === 'miners_rule') {
                payloadParams = { load_events: loadEvents };
            } else {
                // Ensure numbers
                payloadParams = Object.entries(params).reduce((acc, [k, v]) => ({
                    ...acc,
                    [k]: typeof v === 'string' && !isNaN(parseFloat(v)) ? parseFloat(v) : v
                }), {});
            }

            const payload = {
                analysis_type: analysisType,
                parameters: payloadParams
            };
            const data = await authenticatedFetchJSON('/api/v1/lab/advanced-reliability', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            setResult(data);
        } catch (error) {
            console.error(error);
            setResult({ error: "Analysis failed" });
        } finally {
            setLoading(false);
        }
    };

    const updateLoadEvent = (index: number, field: keyof typeof loadEvents[0], value: string) => {
        const newEvents = [...loadEvents];
        newEvents[index] = { ...newEvents[index], [field]: parseFloat(value) || 0 };
        setLoadEvents(newEvents);
    };

    const addLoadEvent = () => setLoadEvents([...loadEvents, { cycles_actual: 0, cycles_to_failure: 0 }]);
    const removeLoadEvent = (index: number) => setLoadEvents(loadEvents.filter((_, i) => i !== index));

    const currentFields = getFields(analysisType);

    return (
        <div className="bg-card border border-border p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Hammer className="text-red-500" /> Advanced Fatigue
                </h2>
                <select
                    value={analysisType}
                    onChange={(e) => {
                        setAnalysisType(e.target.value as FatigueAnalysisType);
                        setParams({});
                        setResult(null);
                    }}
                    className="bg-background border border-border rounded px-2 py-1 text-sm outline-none focus:border-primary"
                >
                    {analysisOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            <div className={`grid grid-cols-1 ${analysisType === 'miners_rule' ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-6`}>
                <div className="space-y-4">

                    {/* Special UI for Miner's Rule */}
                    {analysisType === 'miners_rule' ? (
                        <div className="border border-border rounded p-4">
                            <label className="block text-sm font-medium mb-2">Load Spectrum</label>
                            <div className="space-y-2">
                                <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-text-secondary">
                                    <div>Cycles Applied (n)</div>
                                    <div>Life at Stress (N)</div>
                                    <div>Action</div>
                                </div>
                                {loadEvents.map((event, idx) => (
                                    <div key={idx} className="grid grid-cols-3 gap-2">
                                        <input
                                            type="number"
                                            className="bg-background border border-border rounded px-2 py-1 text-sm"
                                            value={event.cycles_actual || ''}
                                            onChange={e => updateLoadEvent(idx, 'cycles_actual', e.target.value)}
                                        />
                                        <input
                                            type="number"
                                            className="bg-background border border-border rounded px-2 py-1 text-sm"
                                            value={event.cycles_to_failure || ''}
                                            onChange={e => updateLoadEvent(idx, 'cycles_to_failure', e.target.value)}
                                        />
                                        <button onClick={() => removeLoadEvent(idx)} className="text-red-500 hover:bg-red-50 rounded px-2">Remove</button>
                                    </div>
                                ))}
                                <button onClick={addLoadEvent} className="text-sm text-primary hover:underline">+ Add Load Case</button>
                            </div>
                        </div>
                    ) : (
                        /* Standard Fields */
                        currentFields.map(field => (
                            <div key={field.key}>
                                <label className="block text-xs font-medium mb-1 text-text-secondary">{field.label}</label>
                                <input
                                    type="number"
                                    className="w-full bg-background border border-border rounded p-2 text-sm focus:ring-1 focus:ring-primary"
                                    value={params[field.key] || ''}
                                    onChange={e => setParams(prev => ({ ...prev, [field.key]: e.target.value }))}
                                    placeholder={field.placeholder?.toString()}
                                />
                            </div>
                        ))
                    )}

                    <button
                        onClick={handleCalculate}
                        disabled={loading}
                        className="w-full bg-primary text-white py-2 rounded flex justify-center items-center gap-2 hover:bg-primary/90 mt-4 disabled:opacity-50"
                    >
                        {loading ? <Activity className="animate-spin" /> : <Play size={16} />}
                        Analyze Fatigue
                    </button>
                </div>

                {/* Results Section - Full width for Miner's Rule if we wanted, but keeping side-by-side for consistency except Miner's which might need more space if complex */}
                <div className="bg-muted/20 border border-border rounded p-4 h-full min-h-[200px]">
                    <h3 className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">Results</h3>
                    {result ? (
                        <div className="space-y-4">
                            {result.cumulative_damage !== undefined && (
                                <div className={`text-center p-4 bg-background rounded border ${result.cumulative_damage >= 1.0 ? 'border-red-500 bg-red-50' : 'border-green-500 bg-green-50'}`}>
                                    <div className="text-xs text-text-secondary uppercase">Cumulative Damage (D)</div>
                                    <div className={`text-3xl font-bold mt-1 ${result.cumulative_damage >= 1.0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {result.cumulative_damage.toFixed(4)}
                                    </div>
                                    <div className="text-sm font-medium mt-1">
                                        {result.cumulative_damage >= 1.0 ? "FAILURE PREDICTED" : "PASS (No Failure)"}
                                    </div>
                                </div>
                            )}

                            {result.final_crack_size_m !== undefined && (
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center border-b border-border/50 pb-1">
                                        <span className="text-sm">Final Crack Size:</span>
                                        <span className="font-mono font-bold text-red-600">{(result.final_crack_size_m * 1000).toFixed(4)} mm</span>
                                    </div>
                                    <div className="text-xs text-text-secondary">
                                        {result.failure_occurred ? "CRITICAL FAILURE REACHED" : "Crack stable after N cycles"}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-1 mt-4">
                                {Object.entries(result).map(([k, v]) => {
                                    if (k === 'cumulative_damage' || k.includes('crack')) return null;
                                    return (
                                        <div key={k} className="flex justify-between border-b border-border/50 py-1 last:border-0">
                                            <span className="text-xs text-text-secondary capitalize">{k.replace(/_/g, ' ')}</span>
                                            <span className="text-sm font-mono text-text-primary text-right">{String(v)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-text-secondary opacity-50">
                            <AlertOctagon size={32} className="mb-2" />
                            <p className="text-xs">Run prediction to see fatigue life / damage.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FatigueAnalysis;
